import {
  FX_FIRE,
  FX_TOWER_DOWN,
  fxAt,
  fxCount,
  TOWER_KINDS,
  type Frame,
  type TowerKind,
} from "@empire/core-sim";
import { FLIGHT_TICKS } from "./fx.js";

export type BattleCue = TowerKind | "collapse" | "stoneImpact" | "iceImpact";

/** A sound cursor is separate from deterministic visuals. Seeking and paused redraws are silent. */
export class BattleAudioCursor {
  private frames: readonly Frame[] | null = null;
  private position = 0;
  private playing = false;

  step(
    frames: readonly Frame[] | null,
    position: number,
    playing: boolean,
    visible: (tile: number) => boolean
  ): BattleCue[] {
    const previous = this.position;
    const continuous =
      frames === this.frames &&
      this.playing &&
      playing &&
      position > previous &&
      position - previous <= 4;
    this.frames = frames;
    this.position = position;
    this.playing = playing;
    if (!frames || !continuous) return [];
    const cues = new Set<BattleCue>();
    for (
      let f = Math.max(0, Math.floor(previous));
      f <= Math.min(frames.length - 1, Math.ceil(position + FLIGHT_TICKS));
      f++
    ) {
      const frame = frames[f]!;
      for (let i = 0; i < fxCount(frame.fx); i++) {
        const { code, a, c } = fxAt(frame.fx, i);
        if (!visible(a)) continue;
        // The launch and landing share one recorded shot, so impact sound
        // cannot drift from the visual or disclose a hidden weapon.
        if (code === FX_FIRE && f > previous && f <= position) {
          if (TOWER_KINDS[c] === "cannon") cues.add("stoneImpact");
          if (TOWER_KINDS[c] === "frost") cues.add("iceImpact");
        }
        const at = code === FX_FIRE ? f - FLIGHT_TICKS : f;
        if (at <= previous || at > position) continue;
        if (code === FX_FIRE && TOWER_KINDS[c]) cues.add(TOWER_KINDS[c]!);
        else if (code === FX_TOWER_DOWN) cues.add("collapse");
      }
    }
    return [...cues].slice(0, 3);
  }
}
